/**
 * Real-time match scores via Firestore onSnapshot – no polling, no cloud function cost.
 * Only for matches in open tournaments (Firestore rules allow public read).
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

function toClientMillis(ts) {
  if (ts == null) return null;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts === 'number') return ts;
  if (typeof ts?.seconds === 'number') return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
  return null;
}

/** Serialize match for display (Timestamps -> ms) */
function serializeMatch(m) {
  if (!m) return m;
  const out = { ...m };
  ['scheduledDate', 'startedAt', 'completedAt', 'updatedAt'].forEach((f) => {
    if (out[f] != null) {
      const ms = toClientMillis(out[f]);
      if (ms != null) out[f] = ms;
    }
  });
  return out;
}

/**
 * Subscribe to live match updates. Returns map matchId -> match.
 * Unsubscribes when matchIds change or component unmounts.
 */
export function useLiveMatches(matchIds) {
  const [matches, setMatches] = useState({});
  const ids = Array.isArray(matchIds) ? matchIds.filter(Boolean) : [];
  const idsKey = useMemo(() => ids.join(','), [ids]);

  useEffect(() => {
    if (ids.length === 0) {
      setMatches({});
      return undefined;
    }
    const unsubs = ids.map((mid) =>
      onSnapshot(
        doc(db, 'matches', mid),
        (snap) => {
          if (!snap.exists()) return;
          setMatches((prev) => {
            const next = { ...prev };
            const raw = { id: snap.id, ...snap.data() };
            next[mid] = serializeMatch(raw);
            return next;
          });
        },
        (err) => console.warn('[useLiveMatches] snapshot error', mid, err)
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [idsKey]);

  return matches;
}
